import * as React from "react";
import { ChevronLeft, Lock } from "lucide-react";
import { FormProvider, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button, Card, useToast } from "@chotca/ui";
import { type Gender, type Posting } from "@chotca/config";
import {
  DEFAULTS,
  formSchema,
  type FormInput,
  type PostingFormOutput,
} from "./schema";
import {
  collectErrorMessages,
  firstErrorKey,
  reportSubmitError,
} from "./helpers";
import {
  AdvancedSection,
  BenefitsSection,
  ContentSection,
  LocationSection,
  type LocationSectionProps,
  PositionSection,
  SourceUrlSection,
} from "./sections";
import { PostingFormModeProvider } from "./mode";
import {
  PreviewImageUploadProvider,
  type PreviewImageUploader,
} from "./preview-upload";

export type { PostingFormOutput } from "./schema";

/**
 * Map a posting (employer/admin detail response) into the create form's input
 * shape so the edit form can be pre-filled. `position` / `employmentType` are
 * still mapped (rendered read-only) so the resolver stays happy; the API
 * ignores them on update.
 */
export function postingToEditValues(
  posting: Posting & { sourceUrl?: string | null },
): FormInput {
  const r = posting.requirements ?? {};
  const gender =
    r.gender === "MALE" || r.gender === "FEMALE"
      ? (r.gender as Exclude<Gender, "OTHER">)
      : null;
  return {
    ...DEFAULTS,
    venueType: posting.venueType,
    position: posting.position,
    employmentType: posting.employmentType,
    title: posting.title,
    description: posting.description,
    salaryMin: Number(posting.salaryMin),
    salaryMax: Number(posting.salaryMax),
    salaryType: posting.salaryType,
    headcount: posting.headcount,
    benefits: [...posting.benefits],
    skillsRequired: [...posting.skillsRequired],
    skillsPreferred: [...posting.skillsPreferred],
    reqGender: gender,
    reqMinAge: r.minAge ?? null,
    reqMaxAge: r.maxAge ?? null,
    reqMinHeightCm: r.minHeightCm ?? null,
    reqMotorbike: r.motorbikeRequired ?? false,
    reqExcludeTattoo: r.excludeVisibleTattoo ?? false,
    reqKyc: r.kycCccdRequired ?? false,
    softNotes: posting.softNotes ?? "",
    // Edit keeps the existing image by default. The server identifies it by
    // URL only, so on submit the caller re-sends the key only when changed.
    previewImageKey: null,
    previewImageUrl: posting.previewImageUrl ?? null,
    sourceUrl: posting.sourceUrl ?? null,
    addressIds: posting.addresses.map((a) => a.id),
  };
}

/**
 * Map a posting into create-form values for the "đăng lại" (duplicate) flow.
 * Reuses the edit mapping, but: position/employmentType stay editable (the
 * create endpoint accepts them, unlike edit), and the preview image is dropped.
 * The create endpoint needs a fresh R2 `previewImageKey`, which the detail
 * response doesn't expose — carrying only the URL would show a preview that
 * silently wouldn't save, so we start without an image and let the user
 * re-upload if they want one.
 */
export function postingToCreateValues(
  posting: Posting & { sourceUrl?: string | null },
): FormInput {
  return {
    ...postingToEditValues(posting),
    previewImageKey: null,
    previewImageUrl: null,
  };
}

export interface EditPostingFormProps {
  loading: boolean;
  onSubmit: (values: PostingFormOutput) => Promise<void>;
  /** Pre-filled values, usually from `postingToEditValues(posting)`. */
  initialValues: FormInput;
  addressBundle: Omit<LocationSectionProps, "AddressPicker">;
  AddressPicker: import("./sections").AddressPickerComponent;
  /** Floor for the headcount stepper (= committed hires). */
  minHeadcount?: number;
  /** orgAddressIds that cannot be deselected (active interviews booked). */
  lockedAddressIds?: string[];
  /** Per-address active-interview count, for the lock hint. */
  addressInterviewCounts?: Record<string, number>;
  submitLabel?: string;
  /** Desktop back link / cancel. */
  onCancel?: () => void;
  /** Mobile sticky-header back chevron (e.g. nav(-1)). */
  onMobileBack?: () => void;
  /** Presign + PUT handler for the preview image. Omit to hide the field. */
  onUploadPreviewImage?: PreviewImageUploader["onUpload"];
  /** Admin-only: render the Facebook source-link field. Default false. */
  showSourceUrl?: boolean;
}

export function EditPostingForm({
  loading,
  onSubmit,
  initialValues,
  addressBundle,
  AddressPicker,
  minHeadcount,
  lockedAddressIds,
  addressInterviewCounts,
  submitLabel = "Lưu thay đổi",
  onCancel,
  onMobileBack,
  onUploadPreviewImage,
  showSourceUrl,
}: EditPostingFormProps) {
  const { toast } = useToast();

  const form = useForm<FormInput, unknown, PostingFormOutput>({
    resolver: zodResolver(formSchema),
    defaultValues: initialValues,
    mode: "onBlur",
  });
  const { handleSubmit, formState } = form;
  const { isDirty } = formState;

  async function handleFormSubmit(values: PostingFormOutput) {
    try {
      await onSubmit(values);
    } catch (err) {
      reportSubmitError(err, toast);
    }
  }

  function onInvalid(errors: typeof formState.errors) {
    const messages = collectErrorMessages(errors);
    toast({
      tone: "error",
      title: "Vui lòng kiểm tra lại các trường",
      description: messages.length
        ? messages.slice(0, 3).join(" · ")
        : "Có lỗi không xác định",
    });
    const key = firstErrorKey(errors);
    if (key) {
      requestAnimationFrame(() => {
        const el = document.getElementById(`field-${key}`);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
      });
    }
  }

  const errorCount = Object.keys(formState.errors).length;

  return (
    <PostingFormModeProvider
      value={{
        mode: "edit",
        lockPosition: true,
        lockEmploymentType: true,
        minHeadcount,
        lockedAddressIds,
        addressInterviewCounts,
        showSourceUrl,
      }}
    >
      <FormProvider {...form}>
        <PreviewImageUploadProvider
          value={
            onUploadPreviewImage ? { onUpload: onUploadPreviewImage } : null
          }
        >
          <div className="mb-4 flex items-center gap-2 md:hidden">
            {onMobileBack ? (
              <button
                type="button"
                onClick={onMobileBack}
                aria-label="Quay lại"
                className="-ml-2 flex h-10 w-10 items-center justify-center rounded-full text-ink transition-colors active:bg-mist"
              >
                <ChevronLeft size={24} strokeWidth={2} />
              </button>
            ) : null}
            <p className="min-w-0 flex-1 truncate text-base font-extrabold text-ink">
              Chỉnh sửa tin
            </p>
          </div>

          <form
            onSubmit={handleSubmit(handleFormSubmit, onInvalid)}
            className="space-y-6 pb-[calc(6rem+env(safe-area-inset-bottom))] md:pb-24"
          >
            <header className="hidden md:block">
              <h1 className="text-display-lg font-extrabold text-ink">
                Chỉnh sửa tin tuyển dụng
              </h1>
              <p className="mt-1 flex items-center gap-1.5 text-sm text-ink/60">
                <Lock size={14} className="text-ash" />
                Vị trí và loại hình không thể đổi sau khi đăng.
              </p>
            </header>

            <PositionSection />
            <ContentSection />
            <BenefitsSection />
            <LocationSection {...addressBundle} AddressPicker={AddressPicker} />
            <AdvancedSection />
            <SourceUrlSection />
          </form>

          <div className="pointer-events-none sticky bottom-4 z-30 mt-4">
            <div className="pointer-events-auto flex items-center justify-between gap-4 rounded-2xl border-2 border-ink bg-ink px-5 py-3 shadow-[0_12px_32px_rgba(15,15,15,0.25)]">
              <div className="min-w-0">
                {formState.submitCount > 0 && errorCount > 0 ? (
                  <>
                    <p className="text-sm font-extrabold text-ember">
                      {errorCount} lỗi cần sửa
                    </p>
                    <p className="mt-0.5 text-xs text-paper/70">
                      Cuộn lên kiểm tra các trường được đánh dấu đỏ.
                    </p>
                  </>
                ) : (
                  <>
                    <p className="text-sm font-extrabold text-paper">
                      {isDirty ? "Có thay đổi chưa lưu" : "Chưa có thay đổi"}
                    </p>
                    <p className="mt-0.5 text-xs text-paper/70">
                      Thay đổi áp dụng ngay với ứng viên đang xem tin.
                    </p>
                  </>
                )}
              </div>
              <div className="flex shrink-0 items-center gap-2">
                {onCancel ? (
                  <Button
                    type="button"
                    variant="ghost"
                    size="md"
                    className="border-paper/30 text-paper hover:border-paper hover:bg-paper/10"
                    onClick={onCancel}
                    disabled={loading}
                  >
                    Huỷ
                  </Button>
                ) : null}
                <Button
                  type="button"
                  variant="primary"
                  size="lg"
                  loading={loading}
                  disabled={!isDirty}
                  onClick={handleSubmit(handleFormSubmit, onInvalid)}
                >
                  {submitLabel}
                </Button>
              </div>
            </div>
          </div>
        </PreviewImageUploadProvider>
      </FormProvider>
    </PostingFormModeProvider>
  );
}

/** Read-only banner shown in place of the form when a posting isn't editable. */
export function PostingNotEditable({
  state,
  onBack,
}: {
  state: string;
  onBack?: () => void;
}) {
  const label =
    state === "CLOSED"
      ? "Tin đã đóng"
      : state === "EXPIRED"
        ? "Tin đã hết hạn"
        : state === "CANCELLED"
          ? "Tin đã huỷ"
          : state === "DRAFT"
            ? "Tin đang ở dạng nháp"
            : "Tin không thể chỉnh sửa";
  return (
    <Card className="p-6">
      <div className="flex items-start gap-3">
        <Lock size={18} className="mt-0.5 shrink-0 text-ash" />
        <div>
          <p className="text-base font-extrabold text-ink">{label}</p>
          <p className="mt-1 text-sm text-ink/70">
            Chỉ có thể chỉnh sửa tin khi đang mở. Hãy đăng một tin mới nếu cần
            tuyển tiếp.
          </p>
          {onBack ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="mt-3"
              onClick={onBack}
            >
              ← Quay lại tin
            </Button>
          ) : null}
        </div>
      </div>
    </Card>
  );
}
